import { UpdatePersonalDetailsFormData } from '../component/profile/personal-info/personal-details/update-personal-details-form-validation-schema'
import { useSessionContext } from '../context/session-context'
import { Gender, HeightUnit, HustlerProfile, WeightUnit } from '../dao/core-dao'
import { coreService } from '../service/core-service'
import { logger } from '../util/logger'
import { toasterUtil } from '../util/toaster-util'
import { useMutation } from '@tanstack/react-query'
import { UseFormSetError } from 'react-hook-form'

interface UseUpdatePersonalDetailsOptions {
  onSuccess?: () => void
  setError?: UseFormSetError<UpdatePersonalDetailsFormData>
}

export const useUpdatePersonalDetails = (
  options?: UseUpdatePersonalDetailsOptions,
): {
  updatePersonalDetails: (formData: UpdatePersonalDetailsFormData) => void
  isUpdatingPersonalDetails: boolean
} => {
  const { refreshMyData } = useSessionContext()

  const { mutate: updatePersonalDetailsMutation, isLoading: isUpdatingPersonalDetails } = useMutation<
    void,
    Error,
    UpdatePersonalDetailsFormData
  >(
    async (formData: UpdatePersonalDetailsFormData): Promise<void> => {
      const profile: HustlerProfile = {
        username: formData.username,
        gender: formData.gender as Gender | undefined,
        dateOfBirth: formData.dateOfBirth ? new Date(formData.dateOfBirth) : undefined,
        height:
          formData.heightValue && formData.heightValue !== ''
            ? {
                value: parseFloat(formData.heightValue),
                unit: formData.heightUnit as HeightUnit,
              }
            : undefined,
        weight:
          formData.weightValue && formData.weightValue !== ''
            ? {
                value: parseFloat(formData.weightValue),
                unit: formData.weightUnit as WeightUnit,
              }
            : undefined,
      }

      await coreService.updateMe({ profile })
    },
    {
      onSuccess: async () => {
        await refreshMyData()
        toasterUtil.success('Personal details updated successfully')
        if (options?.onSuccess) {
          options.onSuccess()
        }
      },
      onError: (error: Error) => {
        logger.error('Error updating personal details:', error)
        let errorMessage = 'Failed to update personal details. Please try again.'

        if (error && typeof error === 'object' && 'message' in error) {
          errorMessage = error.message
        }

        toasterUtil.error(errorMessage)

        if (options?.setError) {
          options.setError('username', {
            type: 'server',
            message: errorMessage,
          })
        }
      },
    },
  )

  const updatePersonalDetails = (data: UpdatePersonalDetailsFormData): void => {
    updatePersonalDetailsMutation(data)
  }

  return {
    updatePersonalDetails,
    isUpdatingPersonalDetails,
  }
}
